import { getServerSideSplitTreatments } from "@leafly-com/split-next";
import { GetServerSideProps } from "next";
import { useSelector } from "react-redux";
import { JsonLd } from "react-schemaorg";
import { Organization, WebSite } from "schema-dts";

import { getInitialHomePage } from "api/requests/getInitialHomePage";
import { AdMarqueePageLocation } from "custom-types/AdMarquee";
import {
  CampaignSlots,
  HomePageNewsSection,
  NewsletterSection,
  PopularCities as PopularCitiesType,
  ProductCategories,
  ValueProps as ValuePropsType,
} from "custom-types/Directus";
import { DispensaryV3 } from "custom-types/DispensariesV3";
import useDispensariesLink from "hooks/useDispensariesLink";
import { useEventTracker } from "hooks/useEventTracker";
import { getDomainCountryCode } from "redux/selectors/config";
import { determineOrderMethod } from "utils/determineOrderMethod";
import { getMerchId } from "utils/eventTracking";
import { localizeText } from "utils/localizeText";
import { getCountryCodeHeader } from "utils/requestContext";
import { getSplitKey } from "utils/split/getSplitKey";
import { Action, Category } from "utils/trackEvent";

import AdMarqueeCarousel from "components/AdMarqueeCarousel";
import DealsTakeover from "components/DealsTakeover";
import DispensariesCarousel from "components/DispensariesCarousel";
import DownloadAppCTA from "components/DownloadAppCTA";
import FeaturedBrands from "components/FeaturedBrands";
import FeaturedDispensariesSection from "components/FeaturedDispensariesSection";
import FeaturedStrains from "components/FeaturedStrains";
import HomePageCampaignSlots from "components/HomePageCampaignSlots";
import HomePageNewsCta from "components/HomePageNewsCta";
import HomePageProductCategories from "components/HomePageProductCategories";
import MetaTags from "components/MetaTags";
import Newsletter from "components/Newsletter";
import PopularCities from "components/PopularCities";
import SocialImpact from "components/SocialImpact";
import ValueProps from "components/ValueProps";

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const countryCode = getCountryCodeHeader(ctx);

  const data = await getInitialHomePage(countryCode);

  /* NOTE: This treatment is now technically a flag and inflated Split metrics
   * are no longer a concern. Ideally, it'd be renamed to 'shopEveryMenuMVP_flag',
   * but it's already in production and will be sunsetted soon, so the effort
   * outweighs the benefit.
   */
  const {
    shopEveryMenuMVP_test: { treatment: splitTreatment },
    UseCityStateFinder_flag: { treatment: splitTreatmentFinder },
    "b2b-1435_backfillFeaturedHomepage_flag": {
      treatment: splitTreatmentBackfill,
    },
  } = await getServerSideSplitTreatments(
    [
      "shopEveryMenuMVP_test",
      "UseCityStateFinder_flag",
      "b2b-1435_backfillFeaturedHomepage_flag",
    ],
    getSplitKey(ctx)
  );
  const shopEveryMenuMVPFlagOn = splitTreatment === "on";
  const finderCityStateSplitEnabled = splitTreatmentFinder === "on";
  const backfillFeaturedHomepageEnabled = splitTreatmentBackfill === "on";

  return {
    props: {
      ...data,
      backfillFeaturedHomepageEnabled,
      finderCityStateSplitEnabled,
      shopEveryMenuMVPFlagOn,
    },
  };
};

function HomePage({
  campaignSlots,
  finderCityStateSplitEnabled,
  newsletterSections,
  newsSections,
  popularCities,
  productCategories,
  shopEveryMenuMVPFlagOn,
  backfillFeaturedHomepageEnabled,
  valueProps,
}: {
  campaignSlots: CampaignSlots<string>[];
  finderCityStateSplitEnabled: boolean;
  newsletterSections: NewsletterSection[];
  newsSections: HomePageNewsSection<string>[];
  popularCities: PopularCitiesType["cities"];
  productCategories: ProductCategories<string>[];
  shopEveryMenuMVPFlagOn: boolean;
  backfillFeaturedHomepageEnabled: boolean;
  valueProps: ValuePropsType;
}) {
  const countryCode = useSelector(getDomainCountryCode);

  const { publishEvent } = useEventTracker();

  const trackCardImpression = (slot: number, dispensary: DispensaryV3) => {
    const eventLabel = determineOrderMethod(
      !!dispensary.flags.includes("pickup"),
      !!dispensary.flags.includes("delivery"),
      dispensary.tier,
      dispensary.flags
    );

    const merchandisingCampaignId = getMerchId(dispensary, "platinumplacement");

    publishEvent({
      action: Action.impression,
      category: Category.dispensaryCard,
      dispensaryId: dispensary.id,
      label: `slot #${slot + 1}: ${eventLabel}`,
      merchandisingCampaignId,
    });
  };

  return (
    <>
      {/* here for SEO purposes! please dont delete */}
      <h1 className="hidden">Leafly</h1>
      <JsonLd<WebSite>
        item={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Leafly",
          potentialAction: {
            "@type": "SearchAction",
            // @ts-expect-error -- This attribute is documented (https://schema.org/docs/actions.html#part-4), but not specified in the schema-dts package
            "query-input": "required name=search_term_string",
            target: "https://www.leafly.com/search?q={search_term_string}",
          },
          url: "https://www.leafly.com/",
        }}
      />
      <JsonLd<Organization>
        item={{
          "@context": "https://schema.org",
          "@type": "Organization",
          founder: [
            {
              "@type": "Person",
              name: "Cy Scott",
              sameAs: "https://www.linkedin.com/in/cyscott",
            },
            {
              "@type": "Person",
              name: "Scott Vickers",
              sameAs: "https://www.linkedin.com/in/scottvickers",
            },
            {
              "@type": "Person",
              name: "Brian Wansolich",
              sameAs: "https://www.linkedin.com/in/wansolich",
            },
          ],
          logo: "https://public.leafly.com/leafly-logo.png",
          name: "Leafly",
          sameAs: [
            "http://www.facebook.com/leaflydotcom",
            "https://twitter.com/leafly",
            "https://plus.google.com/+leaflydotcom",
            "https://www.youtube.com/channel/UCqVnkns6Gf3W7J9zWZE4EqA",
            "https://en.wikipedia.org/wiki/Leafly",
            "https://www.wikidata.org/wiki/Q6508893",
            "https://www.crunchbase.com/organization/leaf-ly",
            "https://www.linkedin.com/company/leafly/",
          ],
          url: "https://www.leafly.com/",
        }}
      />
      <MetaTags
        canonicalParams={[]}
        title="Find, order, and learn about weed | Leafly"
        description="Leafly is the leading destination to learn about, find, and order cannabis. Learn more about types of weed strains and discover marijuana dispensaries near you."
      />
      <div className="container mt-xl">
        <AdMarqueeCarousel pageType={AdMarqueePageLocation.HOME_PAGE} />
      </div>
      <FeaturedDispensariesSection
        className="border-b border-light-grey py-xxl"
        shopEveryMenuMVPFlagOn={shopEveryMenuMVPFlagOn}
        backfillFeaturedHomepageEnabled={backfillFeaturedHomepageEnabled}
        dispensaryLink={useDispensariesLink({
          finderCityStateServerSideSplitEnabled: finderCityStateSplitEnabled,
        })}
      />
      <HomePageProductCategories
        productCategories={productCategories}
        className="border-b border-light-grey pt-xl pb-lg"
      />
      <FeaturedBrands className="border-b border-light-grey py-xxl" />
      <DealsTakeover
        className="mb-xxl"
        gaCategory="Homepage Deals"
        signupSource="homepage_deals"
      />
      <DispensariesCarousel
        title={`Shop ${localizeText("dispensaries", countryCode)} near you`}
        subtitle="Showing you stores near"
        link={useDispensariesLink({
          finderCityStateServerSideSplitEnabled: finderCityStateSplitEnabled,
        })}
        linkText={`See all ${localizeText("dispensaries", countryCode)}`}
        page="home"
        changeLocation={true}
        className="pb-xxl"
        trackCardImpression={trackCardImpression}
      />
      <FeaturedStrains className="py-xxl" />
      {valueProps && <ValueProps {...valueProps} className="py-xxl" />}
      <DownloadAppCTA
        eventCategory="Homepage App Feature"
        title="The Leafly app is where it’s at"
        subtitle="Over 300,000 reviews on App Store and Google Play"
        description="Your direct line to the world’s largest strain library, over
                    a million strain reviews, award-winning articles, and of
                    course, shopping weed near you."
        utmCampaign="Home"
      />
      <HomePageCampaignSlots
        campaignSlots={campaignSlots}
        className="border-b border-light-grey py-xxl"
      />
      <HomePageNewsCta
        newsSections={newsSections}
        className="border-b border-light-grey py-xxl"
      />
      <SocialImpact className="border-b border-light-grey py-xxl" />
      <Newsletter
        newsletterSections={newsletterSections}
        className="border-b border-light-grey py-xxl"
      />
      <PopularCities cities={popularCities} className="pt-xxl pb-section" />
    </>
  );
}

export default HomePage;
